import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, LogIn, LogOut, Inbox } from "lucide-react";
import { ASSETS, SCHEDULER_URL, MAIN_SITE } from "../lib/constants";
import { useAuth } from "../context/AuthContext";

const PROGRAM_LINKS = [
  { to: "/percussion-pals", label: "Percussion Pals", sub: "Early Childhood" },
  { to: "/percussion-pathways", label: "Percussion Pathways", sub: "Senior & Memory Care" },
  { to: "/drumtalk", label: "DrumTalk", sub: "Corporate & Groups" },
  { to: "/program/trap-learning", label: "TRAP Learning", sub: "Sensory & Different Learners · All Ages" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [acct, setAcct] = useState(false);
  const { user, logout } = useAuth();
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <nav
      data-testid="main-nav"
      className={`fixed top-0 left-0 right-0 z-40 transition-colors duration-500 ${
        scrolled ? "glass" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3">
          <img src={ASSETS.logo} alt="Casey Muze" className="h-11 md:h-14 w-auto" />
          <span className="hidden sm:block font-mono text-[10px] tracking-[0.3em] uppercase text-fg/60">
            Rhythm &amp; Restoration
          </span>
        </Link>
        <div className="flex items-center gap-6">
          <div
            className="relative hidden md:block"
            onMouseEnter={() => setOpen(true)}
            onMouseLeave={() => setOpen(false)}
            data-testid="nav-programs-dropdown"
          >
            <button className="flex items-center gap-1.5 font-mono text-[11px] tracking-[0.2em] uppercase text-fg/70 hover:text-fg transition-colors" data-testid="nav-programs-trigger">
              Explore Offerings <ChevronDown size={13} className={`transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            {open && (
              <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3" data-testid="nav-programs-menu">
                <div className="glass shape-r p-2 w-64 border border-white/10 shadow-2xl">
                  {PROGRAM_LINKS.map((l) => (
                    <Link
                      key={l.to}
                      to={l.to}
                      data-testid={`nav-program-${l.to.split("/").pop()}`}
                      className="block px-4 py-3 rounded-lg hover:bg-white/10 transition-colors"
                    >
                      <span className="block font-medium text-sm">{l.label}</span>
                      <span className="block font-mono text-[9px] tracking-[0.15em] uppercase text-fg/45 mt-0.5">{l.sub}</span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
          <a
            href="https://www.google.com/search?client=safari&rls=en&q=casey+muze&ie=UTF-8&oe=UTF-8"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-about"
            className="hidden md:block font-mono text-[11px] tracking-[0.2em] uppercase text-fg/70 hover:text-fg transition-colors"
          >
            About
          </a>
          <a
            href={MAIN_SITE}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-mainsite"
            className="hidden md:block font-mono text-[11px] tracking-[0.2em] uppercase text-fg/70 hover:text-fg transition-colors"
          >
            Braced No More ↗
          </a>
          {!user ? (
            <Link
              to="/login"
              data-testid="nav-signin-btn"
              className="hidden md:flex items-center gap-1.5 font-mono text-[11px] tracking-[0.2em] uppercase text-fg/70 hover:text-fg transition-colors"
            >
              <LogIn size={13} /> Sign in
            </Link>
          ) : (
            <div
              className="relative hidden md:block"
              onMouseEnter={() => setAcct(true)}
              onMouseLeave={() => setAcct(false)}
              data-testid="nav-account"
            >
              <button data-testid="nav-account-btn" className="flex items-center gap-2" aria-label="Account menu">
                {user.picture ? (
                  <img src={user.picture} alt="" className="h-8 w-8 rounded-full border border-white/15" />
                ) : (
                  <span className="grid place-items-center h-8 w-8 rounded-full bg-primary-c/20 text-primary-c font-mono text-xs">
                    {(user.name || "?").slice(0, 1).toUpperCase()}
                  </span>
                )}
                <ChevronDown size={13} className={`transition-transform text-fg/60 ${acct ? "rotate-180" : ""}`} />
              </button>
              {acct && (
                <div className="absolute right-0 top-full pt-3" data-testid="nav-account-menu">
                  <div className="glass shape-r p-3 w-56 border border-white/10 shadow-2xl">
                    <p className="px-1 text-sm font-medium truncate" data-testid="nav-account-name">{user.name}</p>
                    <p className="px-1 font-mono text-[10px] tracking-[0.1em] text-fg/45 truncate mb-2">{user.email}</p>
                    {user.is_admin && (
                      <Link
                        to="/leads"
                        data-testid="nav-leads-link"
                        className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/10 transition-colors font-mono text-[11px] tracking-[0.15em] uppercase text-fg/80"
                      >
                        <Inbox size={13} /> Leads
                      </Link>
                    )}
                    <button
                      onClick={logout}
                      data-testid="nav-signout-btn"
                      className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/10 transition-colors font-mono text-[11px] tracking-[0.15em] uppercase text-fg/80"
                    >
                      <LogOut size={13} /> Sign out
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          <a
            href={SCHEDULER_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-book-btn"
            className="shape-r bg-primary-c text-bg font-mono text-[11px] tracking-[0.15em] uppercase px-4 py-2.5 hover:opacity-90 transition-opacity"
          >
            Book a Call
          </a>
        </div>
      </div>
    </nav>
  );
}
